import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, CheckCircle, Upload } from 'lucide-react';
import { settingsApi, uploadApi } from '@/services/api';
import { toast } from 'sonner';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    phone: '',
    whatsapp: '',
    email: '',
    address_en: '',
    address_ar: '',
    hero_video_url: '',
    social_links: {
      instagram: '',
      facebook: '',
      twitter: ''
    },
    transformation_before_image: '',
    transformation_after_image: '',
    transformation_title_en: '',
    transformation_title_ar: '',
    agreement_template_en: '',
    agreement_template_ar: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings({
        ...response.data,
        social_links: response.data.social_links || { instagram: '', facebook: '', twitter: '' }
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTransformationUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadApi.image(file);
      setSettings((prev) => ({ ...prev, [field]: res.data.url }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    }
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);

    try {
      await settingsApi.update({ ...settings, id: 'site_settings' });
      setSaved(true);
      toast.success('Settings saved successfully');
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-white/5 w-48" />
        <div className="h-64 bg-white/5" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Site Settings</h1>
        <p className="text-white/60">Configure your website settings</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-8">
        {/* Contact Information */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-6">Contact Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-white/60 text-sm mb-2">Phone Number</label>
              <input
                type="text"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                data-testid="settings-phone"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="+966..."
              />
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">WhatsApp Number</label>
              <input
                type="text"
                value={settings.whatsapp}
                onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })}
                data-testid="settings-whatsapp"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="+966..."
              />
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Email</label>
              <input
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                data-testid="settings-email"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="info@armankitchens.com"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Address (English)</label>
                <input
                  type="text"
                  value={settings.address_en}
                  onChange={(e) => setSettings({ ...settings, address_en: e.target.value })}
                  data-testid="settings-address-en"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Address (Arabic)</label>
                <input
                  type="text"
                  value={settings.address_ar}
                  onChange={(e) => setSettings({ ...settings, address_ar: e.target.value })}
                  data-testid="settings-address-ar"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  dir="rtl"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-6">Social Media Links</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-white/60 text-sm mb-2">Instagram URL</label>
              <input
                type="url"
                value={settings.social_links?.instagram || ''}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  social_links: { ...settings.social_links, instagram: e.target.value } 
                })}
                data-testid="settings-instagram"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="https://instagram.com/..."
              />
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Facebook URL</label>
              <input
                type="url"
                value={settings.social_links?.facebook || ''}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  social_links: { ...settings.social_links, facebook: e.target.value } 
                })}
                data-testid="settings-facebook"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="https://facebook.com/..."
              />
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Twitter/X URL</label>
              <input
                type="url"
                value={settings.social_links?.twitter || ''}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  social_links: { ...settings.social_links, twitter: e.target.value } 
                })}
                data-testid="settings-twitter"
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                placeholder="https://twitter.com/..."
              />
            </div>
          </div>
        </div>

        {/* Hero Video */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-6">Hero Section</h2>
          
          <div>
            <label className="block text-white/60 text-sm mb-2">Hero Video URL (YouTube)</label>
            <input
              type="url"
              value={settings.hero_video_url || ''}
              onChange={(e) => setSettings({ ...settings, hero_video_url: e.target.value })}
              data-testid="settings-hero-video"
              className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
              placeholder="https://youtube.com/watch?v=..."
            />
            <p className="text-white/40 text-sm mt-2">
              Leave empty to use default image slider
            </p>
          </div>
        </div>

        {/* Transformation Section */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-2">Transformation Section</h2>
          <p className="text-white/40 text-sm mb-6">Controls the before/after slider on the homepage</p>

          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Title (English)</label>
                <input
                  type="text"
                  value={settings.transformation_title_en || ''}
                  onChange={(e) => setSettings({ ...settings, transformation_title_en: e.target.value })}
                  data-testid="settings-transformation-title-en"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  placeholder="See The Transformation"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Title (Arabic)</label>
                <input
                  type="text"
                  value={settings.transformation_title_ar || ''}
                  onChange={(e) => setSettings({ ...settings, transformation_title_ar: e.target.value })}
                  data-testid="settings-transformation-title-ar"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  dir="rtl"
                  placeholder="شاهد التحول"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Before Image</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={settings.transformation_before_image || ''}
                  onChange={(e) => setSettings({ ...settings, transformation_before_image: e.target.value })}
                  data-testid="settings-transformation-before"
                  className="flex-1 bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  placeholder="https://..."
                />
                <label className="flex items-center gap-2 bg-white/10 text-white/70 px-4 cursor-pointer hover:bg-white/20 transition-colors">
                  <Upload size={16} />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleTransformationUpload(e, 'transformation_before_image')}
                  />
                </label>
              </div>
              {settings.transformation_before_image && (
                <img
                  src={settings.transformation_before_image}
                  alt="Before preview"
                  className="mt-2 h-24 w-auto object-cover border border-white/10"
                />
              )}
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">After Image</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={settings.transformation_after_image || ''}
                  onChange={(e) => setSettings({ ...settings, transformation_after_image: e.target.value })}
                  data-testid="settings-transformation-after"
                  className="flex-1 bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  placeholder="https://..."
                />
                <label className="flex items-center gap-2 bg-white/10 text-white/70 px-4 cursor-pointer hover:bg-white/20 transition-colors">
                  <Upload size={16} />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleTransformationUpload(e, 'transformation_after_image')}
                  />
                </label>
              </div>
              {settings.transformation_after_image && (
                <img
                  src={settings.transformation_after_image}
                  alt="After preview"
                  className="mt-2 h-24 w-auto object-cover border border-white/10"
                />
              )}
            </div>
          </div>
        </div>

        {/* Agreement Template */}
        <div className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-medium text-white mb-2">Agreement Template</h2>
          <p className="text-white/40 text-sm mb-6">
            Displayed to customers on the Agreement step. HTML formatting is supported (bold, lists, etc.).
          </p>

          <div className="space-y-6">
            <div>
              <label className="block text-white/60 text-sm mb-2">Agreement Text (English)</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  { label: 'B', tag: 'strong' },
                  { label: 'I', tag: 'em' },
                  { label: 'U', tag: 'u' },
                  { label: '• List', tag: 'ul-li' },
                  { label: 'H3', tag: 'h3' },
                ].map(({ label, tag }) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const insert = tag === 'ul-li'
                        ? '<ul>\n  <li>Item 1</li>\n  <li>Item 2</li>\n</ul>'
                        : `<${tag}>text</${tag}>`;
                      setSettings((s) => ({
                        ...s,
                        agreement_template_en: (s.agreement_template_en || '') + insert,
                      }));
                    }}
                    className="px-2.5 py-1 text-xs font-bold bg-white/10 text-white/70 hover:bg-gold/20 hover:text-gold border border-white/10 transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <textarea
                rows={8}
                value={settings.agreement_template_en || ''}
                onChange={(e) => setSettings({ ...settings, agreement_template_en: e.target.value })}
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none font-mono text-sm resize-y"
                placeholder="<h3>Kitchen Agreement</h3>&#10;<p>This agreement is made between...</p>"
                dir="ltr"
              />
              {settings.agreement_template_en && (
                <div className="mt-2 p-4 bg-white/3 border border-white/8 text-white/70 text-sm">
                  <p className="text-white/40 text-xs mb-2 uppercase tracking-wider">Preview</p>
                  <div
                    className="prose prose-invert prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: settings.agreement_template_en }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Agreement Text (Arabic)</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  { label: 'غامق', tag: 'strong' },
                  { label: 'مائل', tag: 'em' },
                  { label: '• قائمة', tag: 'ul-li' },
                  { label: 'عنوان', tag: 'h3' },
                ].map(({ label, tag }) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const insert = tag === 'ul-li'
                        ? '<ul>\n  <li>البند الأول</li>\n  <li>البند الثاني</li>\n</ul>'
                        : `<${tag}>النص</${tag}>`;
                      setSettings((s) => ({
                        ...s,
                        agreement_template_ar: (s.agreement_template_ar || '') + insert,
                      }));
                    }}
                    className="px-2.5 py-1 text-xs font-bold bg-white/10 text-white/70 hover:bg-gold/20 hover:text-gold border border-white/10 transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <textarea
                rows={8}
                value={settings.agreement_template_ar || ''}
                onChange={(e) => setSettings({ ...settings, agreement_template_ar: e.target.value })}
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none font-mono text-sm resize-y"
                placeholder="<h3>اتفاقية المطبخ</h3>&#10;<p>هذه الاتفاقية مبرمة بين...</p>"
                dir="rtl"
              />
              {settings.agreement_template_ar && (
                <div className="mt-2 p-4 bg-white/3 border border-white/8 text-white/70 text-sm" dir="rtl">
                  <p className="text-white/40 text-xs mb-2 uppercase tracking-wider">معاينة</p>
                  <div
                    className="prose prose-invert prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: settings.agreement_template_ar }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={saving}
          data-testid="save-settings-btn"
          whileTap={{ scale: 0.98 }}
          className={`flex items-center justify-center gap-2 w-full py-4 font-medium transition-colors ${
            saved 
              ? 'bg-green-500 text-white' 
              : 'bg-gold text-charcoal hover:bg-gold/90'
          }`}
        >
          {saving ? (
            <span className="w-5 h-5 border-2 border-charcoal border-t-transparent rounded-full animate-spin" />
          ) : saved ? (
            <>
              <CheckCircle size={20} />
              Settings Saved
            </>
          ) : (
            <>
              <Save size={20} />
              Save Settings
            </>
          )}
        </motion.button>
      </form>
    </div>
  );
};

export default AdminSettings;
